import React, { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { QUOTE_FORMS } from '@/data/quoteForms';
import API_URL from '@/config/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ShieldCheck, CheckCircle2 } from 'lucide-react';
import { formatApiError } from '@/utils/apiError';

export default function PublicQuoteForm() {
  const { section: sectionParam } = useParams();
  const [searchParams] = useSearchParams();
  const utmSource = searchParams.get('utm_source') || undefined;
  const section = (sectionParam || 'probound').toLowerCase();
  const config = QUOTE_FORMS[section];

  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ customer_name: '', customer_email: '', customer_phone: '', customer_postcode: '' });
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState(null);
  const [error, setError] = useState('');

  const headerStyle = useMemo(() => (
    config ? { background: `linear-gradient(135deg, #0B0B0E 0%, ${config.primary} 70%)`, borderBottom: '3px solid #D4A24B' } : {}
  ), [config]);

  if (!config) {
    return <div className="p-8 text-center text-gray-600">Unknown service. Try /quote/probound, /quote/proepoxy or /quote/prodecor.</div>;
  }

  const setAnswer = (key, val) => setAnswers(a => ({ ...a, [key]: val }));
  const toggleMulti = (key, val) => setAnswers(a => {
    const arr = Array.isArray(a[key]) ? a[key] : [];
    return { ...a, [key]: arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val] };
  });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    // Light required-field check
    if (!contact.customer_name || !contact.customer_email || !contact.customer_postcode) {
      setError('Please complete your name, email and postcode so we can match you to an Approved Installer.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/public/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...contact,
          section,
          enquiry: answers,
          utm_source: utmSource,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiError(data) || 'Could not submit. Please try again.');
      setReference(data.reference);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- SUCCESS SCREEN ----------
  if (reference) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="text-white" style={headerStyle}>
          <div className="max-w-2xl mx-auto px-4 py-5 flex items-center gap-3">
            <ShieldCheck className="w-6 h-6" style={{ color: '#D4A24B' }} />
            <h1 className="text-lg sm:text-2xl font-bold">Ox Resin · {config.label}</h1>
          </div>
        </div>
        <div className="max-w-2xl mx-auto px-4 py-8" data-testid="quote-success">
          <Card className="p-6 sm:p-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Thank you for contacting Ox Resin</h2>
            <p className="text-gray-600 mt-2">Your enquiry has been received. Your unique reference number is:</p>
            <div className="my-5 inline-block px-6 py-3 rounded-xl text-3xl font-bold tracking-widest" style={{ background: '#0B0B0E', color: '#D4A24B', border: '2px solid #D4A24B' }} data-testid="quote-reference">
              {reference}
            </div>
            <p className="text-sm text-gray-700">
              You&apos;ll shortly receive an email from your <strong>local Ox Resin Approved Installer</strong> with their contact details.
              Please <strong>keep this reference safe</strong> — your installer will quote it back to you so you know they&apos;re verified
              as part of our approved network.
            </p>
            <p className="text-sm text-gray-700 mt-3">
              Only your assigned installer will receive this reference. That&apos;s your peace of mind: you&apos;re dealing with a contractor
              we&apos;ve personally checked, trained and certified.
            </p>
            <div className="mt-4 inline-block bg-amber-50 border-l-4 border-amber-400 rounded-md px-4 py-3 text-sm text-amber-900 text-left">
              🛡 <strong>Every installation by an Ox Approved installer is covered by Ox Resin&apos;s guarantee and warranty.</strong>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              Didn&apos;t get the email within 24 hours? Check your junk folder or call us on <strong>0161 207 0707</strong>.
            </p>
          </Card>
        </div>
      </div>
    );
  }

  // ---------- FORM ----------
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white" style={headerStyle}>
        <div className="max-w-2xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-6 h-6" style={{ color: '#D4A24B' }} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: '#D4A24B' }}>Ox Resin · {config.label}</div>
              <h1 className="text-xl sm:text-2xl font-bold leading-tight">Get a free quote</h1>
            </div>
          </div>
          <p className="text-white/80 text-sm mt-2">{config.tagline}</p>
        </div>
      </div>

      <form onSubmit={submit} className="max-w-2xl mx-auto px-4 py-6 space-y-4" data-testid="quote-form">
        {config.questions.map((q) => (
          <Card key={q.key} className="p-4 sm:p-5">
            <Label className="block text-sm sm:text-base font-bold text-gray-900 mb-3">{q.label}</Label>
            {q.type === 'choice' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {q.options.map(opt => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setAnswer(q.key, opt)}
                    data-testid={`q-${q.key}-${opt}`}
                    className={`text-left text-sm px-3 py-2.5 rounded-lg border-2 transition-colors ${
                      answers[q.key] === opt
                        ? 'border-[var(--accent)] bg-[var(--accent-bg)] text-gray-900 font-semibold'
                        : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'
                    }`}
                    style={{ '--accent': config.primary, '--accent-bg': `${config.primary}15` }}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            )}
            {q.type === 'multi' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {q.options.map(opt => {
                  const checked = (answers[q.key] || []).includes(opt);
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => toggleMulti(q.key, opt)}
                      data-testid={`q-${q.key}-${opt}`}
                      className={`text-left text-sm px-3 py-2.5 rounded-lg border-2 transition-colors ${
                        checked
                          ? 'border-[var(--accent)] bg-[var(--accent-bg)] text-gray-900 font-semibold'
                          : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'
                      }`}
                      style={{ '--accent': config.primary, '--accent-bg': `${config.primary}15` }}
                    >
                      {checked ? '✓ ' : ''}{opt}
                    </button>
                  );
                })}
              </div>
            )}
          </Card>
        ))}

        {/* Contact details */}
        <Card className="p-4 sm:p-5">
          <Label className="block text-sm sm:text-base font-bold text-gray-900 mb-3">Your details</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="qf-name" className="text-xs text-gray-600">Full name *</Label>
              <Input id="qf-name" required data-testid="qf-name" value={contact.customer_name} onChange={(e) => setContact({ ...contact, customer_name: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="qf-postcode" className="text-xs text-gray-600">Postcode *</Label>
              <Input id="qf-postcode" required data-testid="qf-postcode" placeholder="e.g. OX1 1AB" value={contact.customer_postcode} onChange={(e) => setContact({ ...contact, customer_postcode: e.target.value.toUpperCase() })} />
            </div>
            <div>
              <Label htmlFor="qf-email" className="text-xs text-gray-600">Email *</Label>
              <Input id="qf-email" type="email" required data-testid="qf-email" value={contact.customer_email} onChange={(e) => setContact({ ...contact, customer_email: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="qf-phone" className="text-xs text-gray-600">Phone</Label>
              <Input id="qf-phone" type="tel" data-testid="qf-phone" value={contact.customer_phone} onChange={(e) => setContact({ ...contact, customer_phone: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="qf-notes" className="text-xs text-gray-600">Anything else we should know? (optional)</Label>
              <Textarea id="qf-notes" rows={3} data-testid="qf-notes" value={contact.message || ''} onChange={(e) => setContact({ ...contact, message: e.target.value })} />
            </div>
          </div>
        </Card>

        {error && <Card className="p-3 border-red-200 bg-red-50 text-red-700 text-sm">{error}</Card>}

        <Button
          type="submit"
          data-testid="qf-submit"
          disabled={submitting}
          className="w-full h-12 font-bold text-white"
          style={{ background: config.primary }}
        >
          {submitting ? 'Sending…' : 'Get my free quote'}
        </Button>
        <p className="text-[11px] text-gray-500 text-center">
          By submitting, you agree we may share your details with one Ox Resin Approved Installer who will contact you about your project.
        </p>
      </form>
    </div>
  );
}
